<script>
export default {
  name: 'S3PayDepositPanel',
  props: {
    paymentUrl: { type: String, required: true },
    checking: { type: Boolean, default: false },
    providerName: { type: String, default: 'S3Pay' }
  },
  data () {
    return {
      iframeLoaded: false
    }
  },
  computed: {
    safePaymentUrl () {
      try {
        const paymentUrl = new URL(String(this.paymentUrl || '').trim())
        return paymentUrl.protocol === 'https:' ? paymentUrl.href : ''
      } catch (error) {
        return ''
      }
    },
    creditSiteName () {
      const hostname = typeof window === 'undefined' ? '' : String(window.location.hostname || '').toLowerCase()
      if (/fun-?beyond/.test(hostname)) return 'funbeyond'
      if (hostname.includes('vr1game')) return 'vr1game'
      return 'connextcc'
    },
    creditOnlyText () {
      return `ใช้เติมเครดิต ${this.creditSiteName} เท่านั้น`
    }
  },
  watch: {
    safePaymentUrl () {
      this.iframeLoaded = false
    }
  },
  methods: {
    handleFrameLoad () {
      this.iframeLoaded = true
    }
  }
}
</script>

<template>
  <section class="s3pay-panel" aria-live="polite">
    <header class="s3pay-panel__header">
      <div>
        <strong>{{ providerName }}</strong>
        <span>ชำระเงินผ่านหน้าต่างด้านล่าง</span>
      </div>
      <span class="s3pay-panel__status">
        <i v-if="checking" class="fas fa-spinner fa-spin" aria-hidden="true" />
        {{ checking ? 'กำลังตรวจสอบ' : 'รอชำระเงิน' }}
      </span>
    </header>

    <div class="s3pay-panel__frame">
      <div v-if="safePaymentUrl && !iframeLoaded" class="s3pay-panel__loading" role="status">
        <i class="fas fa-spinner fa-spin" aria-hidden="true" />
        <span>กำลังโหลดหน้าชำระเงิน {{ providerName }}</span>
      </div>
      <iframe
        v-if="safePaymentUrl"
        :key="safePaymentUrl"
        class="s3pay-panel__iframe"
        :src="safePaymentUrl"
        :title="`หน้าชำระเงิน ${providerName}`"
        loading="eager"
        referrerpolicy="strict-origin-when-cross-origin"
        allow="clipboard-read; clipboard-write"
        @load="handleFrameLoad"
      />
      <div v-else class="s3pay-panel__error" role="alert">
        ไม่สามารถโหลดลิงก์ชำระเงิน {{ providerName }} ได้
      </div>
      <div
        v-if="safePaymentUrl"
        class="s3pay-panel__watermark"
        role="note"
      >
        {{ creditOnlyText }}
      </div>
    </div>

    <footer class="s3pay-panel__actions">
      <button
        type="button"
        class="s3pay-panel__cancel"
        :disabled="checking"
        @click="$emit('cancel')"
      >
        ยกเลิกรายการเดิมและสร้างรายการใหม่
      </button>
      <button type="button" class="s3pay-panel__close" data-dismiss="modal">
        ปิดหน้าต่าง
      </button>
    </footer>
  </section>
</template>

<style scoped>
.s3pay-panel {
  display: flex;
  flex-direction: column;
  width: calc(100% - 30px);
  height: min(760px, calc(100vh - 80px));
  min-height: 520px;
  margin: 16px 15px 22px;
  overflow: hidden;
  border-radius: 16px;
  background: #0f172a;
  color: #fff;
  font-family: 'Kanit', sans-serif;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.28);
}
.s3pay-panel__header { display: flex; flex: 0 0 auto; align-items: center; justify-content: space-between; gap: 12px; padding: 14px 48px 14px 16px; background: #111827; }
.s3pay-panel__header > div { display: flex; flex-direction: column; }
.s3pay-panel__header strong { color: #86efac; font-size: 16px; }
.s3pay-panel__header span { color: #cbd5e1; font-size: 12px; }
.s3pay-panel__status { white-space: nowrap; }
.s3pay-panel__status i { margin-right: 5px; color: #60a5fa; }
.s3pay-panel__frame { position: relative; isolation: isolate; flex: 1 1 auto; min-height: 0; background: #fff; }
.s3pay-panel__iframe { position: relative; z-index: 1; display: block; width: 100%; height: 100%; border: 0; background: #fff; }
.s3pay-panel__watermark { position: absolute; top: 49%; left: 50%; z-index: 4; width: min(88%, 480px); padding: 5px 8px; transform: translate(-50%, -50%) rotate(-3deg); color: #dc2626; font-size: clamp(16px, 4vw, 22px); font-weight: 700; line-height: 1.25; text-align: center; text-shadow: -1px -1px 0 rgba(255, 255, 255, 0.98), 1px -1px 0 rgba(255, 255, 255, 0.98), -1px 1px 0 rgba(255, 255, 255, 0.98), 1px 1px 0 rgba(255, 255, 255, 0.98), 0 0 8px #fff; pointer-events: none; user-select: none; }
.s3pay-panel__loading,
.s3pay-panel__error { position: absolute; inset: 0; z-index: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 12px; padding: 24px; background: #fff; color: #334155; text-align: center; }
.s3pay-panel__loading i { color: #2563eb; font-size: 30px; }
.s3pay-panel__error { color: #b91c1c; }
.s3pay-panel__actions { display: grid; flex: 0 0 auto; gap: 8px; padding: 10px 12px; background: #111827; }
.s3pay-panel__cancel,
.s3pay-panel__close { width: 100%; padding: 10px 12px; border: 0; border-radius: 9px; font-size: 13px; font-weight: 600; text-align: center; }
.s3pay-panel__cancel { background: #fee2e2; color: #b91c1c; }
.s3pay-panel__cancel:disabled { cursor: wait; opacity: 0.65; }
.s3pay-panel__close { background: #e2e8f0; color: #334155; }
@media (max-width: 991.98px) {
  .s3pay-panel {
    width: 100vw;
    height: 100vh;
    height: 100dvh;
    min-height: 0;
    margin: 0;
    border-radius: 0;
  }
  .s3pay-panel__header { padding-top: max(12px, env(safe-area-inset-top)); }
  .s3pay-panel__actions { padding-bottom: max(10px, env(safe-area-inset-bottom)); }
}
</style>
